import { networkBus } from '@tunasong/models'
import { useEffect, useState } from 'react'

export interface StorageQuota {
  usedBytes: number
  quotaBytes: number
  /** 0..1 */
  ratio: number
  /** true when ratio > 0.85 */
  warning: boolean
}

const WARNING_THRESHOLD = 0.85
const POLL_MS = 60_000

/**
 * Polls `navigator.storage.estimate()` for the user-agent's persistent
 * storage usage and emits `storage-changed` on the networkBus when the
 * ratio crosses (or recrosses) the warning threshold. Returns `null` until
 * the first poll completes, and stays `null` if the API is unavailable.
 *
 * Polled at mount, on `visibilitychange`, and every 60 s thereafter.
 */
export const useStorageQuota = (): StorageQuota | null => {
  const [quota, setQuota] = useState<StorageQuota | null>(null)

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
      return
    }
    let cancelled = false

    const poll = async () => {
      if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
        return
      }
      try {
        const estimate = await navigator.storage.estimate()
        if (cancelled) {
          return
        }
        const usage = estimate.usage ?? 0
        const quotaBytes = estimate.quota ?? 0
        if (quotaBytes === 0) {
          return
        }
        const ratio = usage / quotaBytes
        const next: StorageQuota = {
          usedBytes: usage,
          quotaBytes,
          ratio,
          warning: ratio > WARNING_THRESHOLD,
        }
        setQuota(next)
        networkBus.emit('storage-changed', { ratio })
      } catch {
        // ignore — leave previous value in place
      }
    }

    void poll()
    const id = setInterval(() => {
      void poll()
    }, POLL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return quota
}
